'use client';

import CustomLoading from '@/components/custom-loading';
import { sonnerV2 } from '@/components/custom-toast/sonner-with-template';
import CustomTooltip from '@/components/custom-tooltip';
import { Input } from '@/components/ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { configKeepData5Mins } from '@/config/useQuery';
import { pageCustomerDirectories } from '@/constant/route';
import useMediaQuery from '@/hooks/use-media-query';
import { useURLState } from '@/hooks/use-url-state';
import { scrollElementIntoView } from '@/lib/scroll';
import { cn } from '@/lib/utils';
import { Link, usePathname, useRouter } from '@/navigation';
import { api } from '@/trpc/client';
import { ChevronDown, ListTree, Search, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { mediaBreakpoints } from '../../common';
import { useCustomerSidebarStore } from '../../store';
import { PROCEDURE_FIELDS_PARAM_KEY } from '../../type';

// ===== Type =====
type FieldNode = {
    label: string;
    code: string;
    href?: string;
    children?: FieldNode[];
};

// ===== API response type =====
type FieldApiNode = {
    ID: string;
    Name: string;
    Code: string;
    Childrens?: FieldApiNode[];
};

// ===== Map + sort alphabetically =====
function mapFieldToNode(node: FieldApiNode): FieldNode {
    const sortedChildren = node.Childrens
        ? [...node.Childrens].sort((a, b) =>
              a.Name.localeCompare(b.Name, 'vi', { sensitivity: 'base' })
          )
        : [];

    return {
        code: node.Code,
        label: node.Name,
        children:
            sortedChildren.length > 0
                ? sortedChildren.map(mapFieldToNode)
                : undefined,
    };
}

// ===== Filter tree by search query =====
function filterTree(nodes: FieldNode[], query: string): FieldNode[] {
    if (!query.trim()) return nodes;
    const q = query.toLowerCase().trim();

    return nodes.reduce<FieldNode[]>((acc, node) => {
        const matchSelf = node.label.toLowerCase().includes(q);
        const filteredChildren = node.children
            ? filterTree(node.children, query)
            : [];

        if (matchSelf) {
            // Nếu node gốc match → giữ nguyên cả cây con
            acc.push(node);
        } else if (filteredChildren.length > 0) {
            // Nếu con có match → giữ node gốc nhưng chỉ hiện con match
            acc.push({ ...node, children: filteredChildren });
        }

        return acc;
    }, []);
}

// ===== Main Component =====
export default function FieldTreeFilter() {
    const transDef = useTranslations('System');
    const transProcedure = useTranslations('ProcedureManagement');

    const pathname = usePathname();
    const apiUtils = api.useUtils();

    const isDesktop = useMediaQuery(`(min-width: ${mediaBreakpoints}px)`);

    const { isShowSidebar: isOpen } = useCustomerSidebarStore();

    const [isLoadingField, setIsLoadingField] = useState(true);
    const [fieldNodes, setFieldNodes] = useState<FieldNode[]>([]);
    const [total, setTotal] = useState<number | undefined>();
    const [search, setSearch] = useState('');

    const onLoadField = useCallback(async () => {
        setIsLoadingField(true);
        const result = await apiUtils.field.GetTree.fetch(
            { numOfParentBefore: 0, treeDepth: 5 },
            configKeepData5Mins
        );

        if (result.isSuccessStatus) {
            setFieldNodes(
                ((result.data?.Result ?? []) as FieldApiNode[]).map(
                    mapFieldToNode
                )
            );
            setTotal(result.data?.Total);
        } else {
            setFieldNodes([]);
            setTotal(undefined);
            sonnerV2({
                type: 'error',
                title: transDef('System_AnErrorHasOccurred'),
                desc: result.errors,
            });
        }

        setIsLoadingField(false);
    }, [apiUtils.field.GetTree, transDef]);

    useEffect(() => {
        onLoadField();
    }, [onLoadField]);

    const displayNodes = useMemo(
        () => filterTree(fieldNodes, search),
        [fieldNodes, search]
    );

    const _content = (
        <>
            {/* Header */}
            <div
                className={cn({
                    'sticky top-0 z-10': !isOpen && isDesktop,
                })}
            >
                <div className='flex items-center gap-2 whitespace-nowrap px-2 pr-2 text-foreground'>
                    {total && (
                        <div className='flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-sm text-secondary-foreground'>
                            {total}
                        </div>
                    )}
                    <CustomTooltip.InfoIcon
                        tooltipContentProps={{ side: 'right' }}
                        title={
                            <div className='w-96 text-balance'>
                                {transProcedure(
                                    'ProcedureManagement_FieldDesc'
                                )}
                            </div>
                        }
                    >
                        <span className='text-sm font-bold uppercase tracking-widest'>
                            {transProcedure('ProcedureManagement_Field')}
                        </span>
                    </CustomTooltip.InfoIcon>
                </div>
            </div>
            <div
                className={cn(
                    // fieldNodes.length > 0
                    //     ? 'border-primary'
                    //     : 'border-background',
                    'pr-3 pt-2'
                    // 'ml-5 border-l-4 pl-5 pr-3 pt-2'
                )}
            >
                {/* Search input */}
                <div className='pb-1'>
                    <div className='relative'>
                        <Search
                            size={13}
                            strokeWidth={2.5}
                            className='text-gray-350 pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400'
                        />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={
                                transDef('System_EnterToSearch') + '...'
                            }
                            className={cn(
                                'no-ring h-9 w-full rounded-md border border-gray-200 bg-gray-50',
                                'py-1.5 pl-7 pr-7 text-sm text-gray-800',
                                'placeholder:text-gray-400',
                                'outline-none transition-all duration-150',
                                'focus:border-orange-300 focus:bg-white focus:ring-2 focus:ring-orange-100'
                            )}
                        />
                        {search && (
                            <button
                                onClick={() => setSearch('')}
                                className='absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                            >
                                <X size={12} strokeWidth={2.5} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Tree */}
                <div className='flex-1'>
                    <CustomLoading.Wrapper loading={isLoadingField}>
                        <div className='flex min-h-52 flex-col gap-1 pb-3 pl-1'>
                            {displayNodes.length > 0
                                ? displayNodes.map((field, idx) => (
                                      <TreeNode
                                          key={field.code}
                                          node={field}
                                          pathname={pathname}
                                          index={idx}
                                          forceOpen={!!search.trim()}
                                      />
                                  ))
                                : !isLoadingField && (
                                      <div className='px-3 py-6 text-center text-sm text-gray-500'>
                                          {transDef('System_NoDataFound')}
                                      </div>
                                  )}
                        </div>
                    </CustomLoading.Wrapper>
                </div>
            </div>
        </>
    );

    if (!isOpen && isDesktop) {
        return (
            <div className='pt-2'>
                <PopoverField>
                    <div className='pt-3'>{_content}</div>
                </PopoverField>
            </div>
        );
    }

    return _content;
}

function PopoverField(props: PropsWithChildren) {
    const [isOpenPopover, setIsOpenPopover] = useState(false);

    return (
        <Popover open={isOpenPopover} onOpenChange={setIsOpenPopover}>
            <PopoverTrigger
                className={cn(
                    'flex h-10 w-full items-center justify-center rounded p-2 text-gray-700 hover:bg-color-1/50 hover:text-color-1',
                    {
                        'bg-color-1 text-color-1-foreground hover:bg-color-1 hover:text-color-1-foreground hover:opacity-80':
                            isOpenPopover,
                    }
                )}
            >
                <ListTree size={16} strokeWidth={2.5} />
            </PopoverTrigger>

            <PopoverContent
                side='right'
                align='start'
                className='w-[400px] rounded-md p-0 shadow-md'
            >
                <ScrollArea className='h-[60vh] bg-white'>
                    {props.children}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
}

// ===== Highlight matched text =====
function HighlightText({ text, query }: { text: string; query: string }) {
    if (!query.trim()) return <>{text}</>;

    const q = query.trim();
    const regex = new RegExp(
        `(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
        'gi'
    );
    const parts = text.split(regex);

    return (
        <>
            {parts.map((part, i) =>
                regex.test(part) ? (
                    <mark
                        key={i}
                        className='rounded-sm bg-orange-200 px-0.5 not-italic text-orange-800'
                    >
                        {part}
                    </mark>
                ) : (
                    <span key={i}>{part}</span>
                )
            )}
        </>
    );
}

// ===== Recursive Node =====
function TreeNode(props: {
    node: FieldNode;
    depth?: number;
    pathname: string;
    index?: number;
    forceOpen?: boolean;
    searchQuery?: string;
}) {
    const {
        node,
        depth = 0,
        pathname,
        index = 0,
        forceOpen = false,
        searchQuery = '',
    } = props;

    const divRef = useRef<HTMLDivElement>(null);

    const isDesktop = useMediaQuery(`(min-width: ${mediaBreakpoints}px)`);
    const setIsShowMenuOverlay = useCustomerSidebarStore(
        (x) => x.setIsShowMenuOverlay
    );

    const router = useRouter();
    const [fieldCodes, setFieldCodes] = useURLState(PROCEDURE_FIELDS_PARAM_KEY);

    const hasChildren = node.children && node.children.length > 0;
    const isActive = fieldCodes === node.code;
    const isSubActive =
        hasChildren && node.children!.some((c) => c.code === fieldCodes);
    const [isOpen, setIsOpen] = useState((isActive || depth === 0) ?? false);

    // Khi search → tự mở hết; khi xóa search → về trạng thái cũ
    useEffect(() => {
        if (forceOpen) setIsOpen(true);
    }, [forceOpen]);

    // Khi node lĩnh vực hiện tại được kích hoạt → kéo vào vùng nhìn của
    // ScrollArea sidebar (cuộn ngắn nhất, không animation, chỉ cuộn trong
    // sidebar chứ không kéo cả layout chính lên).
    useEffect(() => {
        if (divRef.current && isActive) {
            scrollElementIntoView(divRef.current, {
                block: 'nearest',
                behavior: 'auto',
            });
        }
    }, [isActive]);

    const onClickField = () => {
        let codes: string[] = [node.code];

        // if (node.children&& node.children.length) {
        //     codes.push(...node.children.map(c => c.code))
        // }

        const realCode = codes.join(',');
        if (pathname.includes('/procedure-list')) {
            setFieldCodes((st) => (st === realCode ? '' : realCode));
        } else {
            const pathToPush = `${pageCustomerDirectories.ProcedureList}?${PROCEDURE_FIELDS_PARAM_KEY}=${realCode}`;

            // Nếu đang ở page khác, click vào field sẽ dẫn đến page procedure list với filter tương ứng
            router.push(pathToPush);
        }

        // Nếu đang ở mobile, click vào field sẽ tự động đóng sidebar để hiển thị content
        if (!isDesktop) {
            setIsShowMenuOverlay(false);
        }
    };

    const accentColor = depth === 0 && hasChildren ? 'bg-orange-400' : null;

    const content = (
        <div className='flex w-full items-center gap-x-1'>
            <div
                className={cn('flex flex-1 items-center gap-x-2 p-2', {
                    'px-2': depth !== 0,
                })}
            >
                <span
                    className={cn(
                        'line-clamp-2 flex-1 text-left leading-snug',
                        depth === 0 && hasChildren
                            ? 'line-clamp-1 pr-2 text-sm font-semibold'
                            : 'text-sm font-medium'
                    )}
                >
                    <HighlightText text={node.label} query={searchQuery} />
                </span>
            </div>

            {hasChildren && (
                <div
                    className={cn(
                        'flex flex-shrink-0 items-center gap-0.5 bg-primary py-2 pr-1',
                        'relative z-[1] after:absolute after:inset-y-0 after:left-0 after:-z-[1] after:w-4 after:-translate-x-3 after:rounded-l-full after:bg-primary after:content-[""]'
                    )}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen((st) => !st);
                    }}
                >
                    <span
                        className={cn(
                            'block min-w-4 rounded-full text-center text-[10px] font-semibold tabular-nums text-primary-foreground'
                        )}
                    >
                        {node.children!.length}
                    </span>
                    <ChevronDown
                        strokeWidth={2.5}
                        className={cn(
                            'h-3.5 w-3.5 text-gray-300 transition-transform',
                            isOpen ? 'rotate-180' : ''
                        )}
                    />
                </div>
            )}
        </div>
    );

    const itemClassName = cn(
        'group flex rounded-lg w-full items-center  overflow-hidden transition-all duration-150 cursor-pointer font-medium hover:bg-color-1/50',
        'shadow-sm',
        depth === 0 && hasChildren ? 'bg-color-1/50' : '',
        isActive
            ? 'bg-color-1 text-color-1-foreground hover:bg-color-1 hover:opacity-80 shadow-lg'
            : depth === 0 && hasChildren
              ? 'text-gray-700 font-semibold'
              : 'text-muted-foreground',

        isSubActive
            ? 'bg-color-1/90 text-color-1-foreground hover:bg-color-1 hover:opacity-80 shadow-md'
            : ''
    );

    return (
        <div
            ref={divRef}
            className={cn(depth === 0 && hasChildren && 'mb-0.5')}
        >
            <Link
                href={'#'}
                onClick={(e) => {
                    e.preventDefault();
                    onClickField();
                }}
                className={itemClassName}
            >
                {content}
            </Link>

            {hasChildren && (
                <div
                    className={cn(
                        'overflow-hidden transition-all ease-out',
                        isOpen
                            ? 'max-h-[9999px] opacity-100'
                            : 'max-h-0 opacity-0'
                    )}
                >
                    <div
                        className='relative mt-0.5 flex flex-col gap-y-1.5 py-0.5'
                        style={{ paddingLeft: `${(depth + 1) * 12 + 10}px` }}
                    >
                        <div
                            className={cn(
                                'absolute bottom-2 top-2 w-0.5',
                                depth === 0 && hasChildren
                                    ? cn(accentColor, 'opacity-30')
                                    : 'bg-gray-200'
                            )}
                            style={{ left: `${(depth + 1) * 12 + 2}px` }}
                        />

                        {node.children!.map((child, idx) => (
                            <TreeNode
                                key={child.code}
                                node={child}
                                depth={depth + 1}
                                pathname={pathname}
                                index={index}
                                forceOpen={forceOpen}
                                searchQuery={searchQuery}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
